import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
	ICustomer,
	IOpenBills,
	IReservation,
	IReservationState,
} from "./reservationInterface";
import { findReservation, getIndex } from "../../helpers/reservation";
import { updateReservationState } from "./reservationApi";

const initialState: IReservationState = {
	ActiveReservation: "",
	IsGroup: false,
	Reservations: [],
	OpenBills: null,
	LightScene: 1,
	AccountId: "",
	FirstNames: "",
};

// Update reservation state
export const _updateReservationState = createAsyncThunk(
	"reservation/updateReservationState",
	async (
		{
			reservationId,
			state,
		}: {
			reservationId: IReservation["ReservationId"];
			state: IReservation["State"];
		},
		{ getState },
	) => {
		const response = await updateReservationState(reservationId, state);
		return response.data;
	},
);

export const reservationSlice = createSlice({
	name: "reservation",
	initialState,
	reducers: {
		setReservations: (state, action: PayloadAction<IReservation[]>) => {
			state.IsGroup = action.payload.length > 1;
			state.Reservations = action.payload;
		},
		setOpenBills: (state, action: PayloadAction<IOpenBills>) => {
			state.OpenBills = action.payload;
		},
		setAccountId: (
			state,
			action: PayloadAction<IReservationState["AccountId"]>,
		) => {
			state.AccountId = action.payload;
		},
		setFirstNames: (
			state,
			action: PayloadAction<IReservationState["FirstNames"]>,
		) => {
			state.FirstNames = action.payload;
		},
		setActiveReservation: (
			state,
			action: PayloadAction<IReservation["ReservationId"]>,
		) => {
			state.ActiveReservation = action.payload;
		},
		updateActiveReservation: (state, action: PayloadAction<IReservation>) => {
			let reservations = [...state.Reservations];
			const reservationIndex = getIndex(reservations, state.ActiveReservation);
			reservations[reservationIndex] = action.payload;
			state.Reservations = reservations;
		},
		clearReservations: (state) => {
			state.ActiveReservation = "";
			state.Reservations = [];
			state.OpenBills = null;
			state.LightScene = 1;
		},
		updateCompagnion: (
			state,
			action: PayloadAction<{
				customer: ICustomer;
				customerIndex: number;
			}>,
		) => {
			let reservations = [...state.Reservations];
			const reservationIndex = getIndex(reservations, state.ActiveReservation);
			reservations[reservationIndex].Compagnions[action.payload.customerIndex] =
				action.payload.customer;
			state.Reservations = reservations;
		},
		updateCompagnions: (state, action: PayloadAction<ICustomer[]>) => {
			let reservations = [...state.Reservations];
			const reservationIndex = getIndex(reservations, state.ActiveReservation);
			reservations[reservationIndex].Compagnions = action.payload;
			state.Reservations = reservations;
		},
		updateCompagnionTokenId: (
			state,
			action: PayloadAction<{
				tokenId: ICustomer["TokenId"];
				customerIndex: number;
			}>,
		) => {
			let reservations = [...state.Reservations];
			const reservationIndex = getIndex(reservations, state.ActiveReservation);
			reservations[reservationIndex].Compagnions[action.payload.customerIndex][
				"TokenId"
			] = action.payload.tokenId;
			state.Reservations = reservations;
		},
		setLightScene: (
			state,
			action: PayloadAction<IReservationState["LightScene"]>,
		) => {
			state.LightScene = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(_updateReservationState.fulfilled, (state, action) => {
			console.log(action);

			state.Reservations = action.payload.Reservations;
		});
	},
});

export const {
	setReservations,
	setOpenBills,
	setAccountId,
  setFirstNames,
	setActiveReservation,
	updateActiveReservation,
	clearReservations,
	updateCompagnion,
	updateCompagnions,
	updateCompagnionTokenId,
	setLightScene,
} = reservationSlice.actions;

// Selectors
export const selectReservation = (state: RootState) => state.reservation;
export const selectActiveReservation = (state: RootState) => {
	return findReservation(state.reservation.ActiveReservation);
};
export const selectReservations = (state: RootState) =>
	state.reservation.Reservations;
export const selectOpenBills = (state: RootState) =>
	state.reservation.OpenBills;

export default reservationSlice.reducer;
